import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router";

import EditDatabaseEntry from "./EditDatabaseEntry";
import { parseAndValidWorks } from "./parseAndValidWorks";
import { SonicWorkInputData } from "./SonicWorkInputData";
import { ActivityHistoryData } from "../../../../common/classes/ActivityHistoryData";
import RequestCompleteNotice from "../../../../common/settings-modal/RequestCompleteNotice";

import {
  genTermOptions,
  getCurrentImage,
} from "../../../../utils/GeneralUtils";
import { FieldState } from "../../../../utils/ValidationFunctions";
import { RequestState } from "../../../../utils/RequestState";

import { useAppSelector, useAppDispatch } from "../../../../redux/hooks";
import { getAuthToken } from "../../../../redux/reducers/auth";
import {
  requestTerms,
  getTermsData,
} from "../../../../redux/reducers/taxonomy";
import { updateOwnDBEntry } from "../../../../redux/reducers/user";
import { SonicTermData } from "../../../../common/classes/SonicTerm";
import {
  fieldStateToUpdateArgs,
  validFieldState,
} from "../FieldStateFunctions";
import {
  SonicGameDetail,
  SonicGameDetailData,
} from "../../../../common/classes/SonicGameDetail";
import { useGetGameSelection } from "../../../../utils/useGameSelection";

const CreateDatabaseEntryContainer: React.FC = () => {
  const [fieldState, setFieldState] = useState<FieldState>();
  const [error, setError] = useState<boolean>(false);
  const [isComplete, setIsComplete] = useState<boolean>(false);
  const [requestState, setRequestState] = useState<RequestState>(
    RequestState.Waiting
  );

  const auth_token = useAppSelector(getAuthToken);
  const terms = useAppSelector(getTermsData);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const completeHandler = () => {
    navigate("/mypage/settings", { replace: true });
  };

  const handleInputChange = (
    value: File | string | undefined,
    name: string
  ) => {
    let f: FieldState = {};
    if (fieldState !== undefined) {
      f = fieldState;
    }

    if (value !== undefined) {
      f[name] = [value, true];
      setRequestState(RequestState.Pending);
    } else {
      if (name !== "sonic_imgs") f[name] = ["", false];
    }
    setFieldState({ ...f });
  };

  const editSubmitHandler = () => {
    if (auth_token) {
      const update_args: { [key: string]: string | File } = {};

      if (!validFieldState("create", ["sonic_name"], fieldState)) {
        setError(true);
        return;
      }

      if (!validPlayerHistory(update_args)) {
        setError(true);
        return;
      }

      if (!parseAndValidWorks(update_args, workItems)) {
        setError(true);
        return;
      }

      if (games && games.length > 0) {
        const gameIds: Array<string> = [];
        games.forEach((game) => gameIds.push(game.id));
        update_args["sonic_game"] = JSON.stringify(gameIds);
      }

      if (team) {
        update_args["sonic_team"] = team.id;
      }

      if (gameDetails.length > 0) {
        update_args["sonic_game_details"] = JSON.stringify(
          SonicGameDetail.toRequestArgs(gameDetails, gameSelections)
        );
      }

      fieldStateToUpdateArgs(update_args, fieldState);

      setError(false);
      setRequestState(RequestState.Doing);

      dispatch(
        updateOwnDBEntry({
          auth_token: auth_token,
          update: update_args,
        })
      ).finally(() => setIsComplete(true));
    }
  };

  useEffect(() => {
    dispatch(requestTerms({ types: ["player_types"] }));
  }, []);

  // MyGame,Team
  const [games, setGames] = useState<Array<SonicTermData>>([]);
  const [team, setTeam] = useState<SonicTermData>();

  const handleGames = (value: Array<SonicTermData>) => {
    setGames([...value]);
    setRequestState(RequestState.Pending);
  };

  const handleTeam = (value: SonicTermData) => {
    setTeam(value);
    if (team !== value) {
      setRequestState(RequestState.Pending);
    }
  };

  // Player History
  const [historyItems, setHistoryItems] = useState<Array<ActivityHistoryData>>(
    []
  );

  const addHistoryInput = () => {
    const newList = historyItems?.concat([
      { year: "", title: "", error: false },
    ]);
    setHistoryItems(newList);
  };

  const deleteHistory = (i: number) => {
    const newList = historyItems.filter((v, index) => index !== i);
    setHistoryItems(newList);
  };

  const handleHistoryInputChange = (
    value: string | undefined,
    name: string
  ) => {
    const history = historyItems;
    const field = name.split("_");
    const index = +field[1];
    const prop = field[0];

    switch (prop) {
      case "year":
        history[index].year = value;
        break;
      case "title":
        history[index].title = value;
        break;
      default:
        break;
    }

    if (!history[index].title && history[index].year) {
      history[index].error = true;
    } else {
      history[index].error = false;
      setRequestState(RequestState.Pending);
    }
    setHistoryItems(history);
  };

  const validPlayerHistory = (update_args: {
    [key: string]: string | File;
  }) => {
    if (historyItems.length > 0) {
      const update_obj: Array<{ year?: string; title: string }> = [];
      historyItems.forEach((el) => {
        if (el.error == true) {
          return false;
        }

        if (el.title) {
          update_obj.push({ year: el.year, title: el.title });
        }
      });

      if (update_obj.length == 0) {
        return true;
      }

      update_args["sonic_history"] = JSON.stringify(update_obj);
      return true;
    } else {
      return true;
    }
  };

  const [isEditWork, setIsEditWork] = useState<boolean>(false);
  const [editWorkNumber, setEditWorkNumber] = useState<number>(0);
  const [workItems, setWorkItems] = useState<Array<SonicWorkInputData>>([]);

  const addWorkInput = () => {
    if (workItems.length > 0) {
      setEditWorkNumber(workItems.length);
    } else {
      setEditWorkNumber(0);
    }

    setIsEditWork(true);
  };

  const deleteWork = (i: number) => {
    const newList = workItems.filter((v, index) => index !== i);
    setWorkItems(newList);
  };

  const editWork = (i: number, work: SonicWorkInputData) => {
    const newList = workItems;
    newList[i] = work;
    setWorkItems([...newList]);
  };

  const { gameSelections, isLoading: isGameDetailLoading } =
    useGetGameSelection();
  const [gameDetails, setGameDetails] = useState<Array<SonicGameDetailData>>(
    []
  );

  useEffect(() => {
    if (error) {
      setError(false);
    }
  }, [fieldState, historyItems, workItems]);

  if (isComplete) {
    return (
      <RequestCompleteNotice
        handler={completeHandler}
        backBarText="プロフィール公開申請"
        title="公開申請完了"
        text={
          "公開申請が完了しました。\nこれより審査を開始します。\n結果通知までしばらくお待ちください。"
        }
        sub_text="※内容によっては数日お時間をいただく可能性がございます。"
      />
    );
  } else {
    return (
      <EditDatabaseEntry
        error={error}
        handleSubmit={editSubmitHandler}
        handleInputChange={handleInputChange}
        requestState={requestState}
        isHasChanges={
          fieldState !== undefined || workItems?.length > 0 || games?.length > 0
            ? true
            : false
        }
        editImg={
          fieldState?.["sonic_imgs"]?.[0]
            ? getCurrentImage(fieldState["sonic_imgs"][0])
            : undefined
        }
        typeOptions={genTermOptions(terms, "player_types")}
        games={games}
        handleGames={handleGames}
        team={team}
        handleTeam={handleTeam}
        historyItems={historyItems}
        addHistoryInput={addHistoryInput}
        deleteHistory={deleteHistory}
        handleHistoryInputChange={handleHistoryInputChange}
        workItems={workItems}
        isEditWork={isEditWork}
        editWorkNumber={editWorkNumber}
        addWork={addWorkInput}
        deleteWork={deleteWork}
        editWork={editWork}
        setIsEditWork={setIsEditWork}
        setEditWorkNumber={setEditWorkNumber}
        gameDetailLoading={isGameDetailLoading}
        gameDetailOptions={gameSelections}
        handleGameDetails={(details: Array<SonicGameDetailData>) =>
          setGameDetails([...details])
        }
      />
    );
  }
};

export default CreateDatabaseEntryContainer;
